/** Convert a local recording to bounded 16kHz mono PCM. No network in this module. */
export async function recordingToWav(blob: Blob): Promise<string> {
  if (blob.size > 3_000_000) throw new Error('Choose a recording shorter than 20 seconds.');
  const original = await blob.arrayBuffer();
  if (original.byteLength >= 44 && original.byteLength <= 640044) {
    const view = new DataView(original),
      tag = (n: number) => String.fromCharCode(...new Uint8Array(original, n, 4));
    if (
      tag(0) === 'RIFF' &&
      tag(8) === 'WAVE' &&
      tag(12) === 'fmt ' &&
      view.getUint32(16, true) === 16 &&
      view.getUint16(20, true) === 1 &&
      view.getUint16(22, true) === 1 &&
      view.getUint32(24, true) === 16000 &&
      view.getUint16(34, true) === 16 &&
      tag(36) === 'data' &&
      view.getUint32(40, true) === original.byteLength - 44
    ) {
      let text = '';
      const bytes = new Uint8Array(original);
      for (let i = 0; i < bytes.length; i += 8192)
        text += String.fromCharCode(...bytes.subarray(i, i + 8192));
      return btoa(text);
    }
  }
  const context = new AudioContext();
  try {
    const source = await context.decodeAudioData(original).catch(() => {
      throw new Error('That recording could not be read. Try a short audio file.');
    });
    if (source.duration < 0.1 || source.duration > 20.1)
      throw new Error('Keep your recording under 20 seconds.');
    const frames = Math.min(320000, Math.ceil(source.duration * 16000)),
      offline = new OfflineAudioContext(1, frames, 16000),
      node = offline.createBufferSource();
    node.buffer = source;
    node.connect(offline.destination);
    node.start();
    const pcm = (await offline.startRendering()).getChannelData(0),
      buffer = new ArrayBuffer(44 + pcm.length * 2),
      view = new DataView(buffer),
      word = (offset: number, text: string) => {
        for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
      };
    word(0, 'RIFF');
    view.setUint32(4, buffer.byteLength - 8, true);
    word(8, 'WAVE');
    word(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, 16000, true);
    view.setUint32(28, 32000, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    word(36, 'data');
    view.setUint32(40, pcm.length * 2, true);
    for (let i = 0; i < pcm.length; i++) {
      const n = Math.max(-1, Math.min(1, pcm[i]));
      view.setInt16(44 + i * 2, n * (n < 0 ? 32768 : 32767), true);
    }
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let start = 0; start < bytes.length; start += 8192)
      binary += String.fromCharCode(...bytes.subarray(start, start + 8192));
    return btoa(binary);
  } finally {
    await context.close();
  }
}
